import {
  Alert,
  AnimatedContentLoader,
  Button,
  DropdownMenu,
  FileSelector,
  Input,
  ModalV2,
  Select,
  SingleColumnLayout
} from '@components'
import { InputGroup } from '@components/Inputs/InputGroup.tsx'
import {
  getExpenseClaimExpensePageUrl,
  getExpenseClaimPageUrl,
  getExpenseClaimTripPageUrl
} from '@constants'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import {
  ExpenseCategory,
  ExpenseClaimExpense,
  ExpenseClaimExpenseWrite,
  ExpenseVatRow,
  appendExpenseClaimReceiptPage,
  createExpenseClaimExpense,
  createExpenseClaimExpenseFromAttachment,
  deleteExpenseClaimExpense,
  expenseClaimFieldError,
  fetchExpenseClaim,
  fetchExpenseClaimExpense,
  fetchExpenseClaimReceipt,
  fetchExpenseClaimTrips,
  replaceExpenseClaimReceipt,
  replaceExpenseClaimReceiptFromAttachment,
  updateExpenseClaimExpense,
  useCurrentUser
} from '@query'
import { AttachmentDropHint } from '@root/containers/AttachmentSelector/AttachmentSelector'
import { StyledEmptyAttachmentSelector } from '@root/containers/AttachmentSelector/AttachmentSelector.styled'
import { TagSelector } from '@root/containers/TagSelector/TagSelector'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { FaCaretDown, FaReceipt } from 'react-icons/fa'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import styled from 'styled-components'
import {
  Card,
  Columns,
  Help,
  MainColumn,
  PageContent,
  PageScroll,
  PageSubtitle,
  PageTitle,
  PageTop,
  PageTopMain,
  TextLink,
  claimKey,
  expenseClaimExpensesKey
} from './shared'

const CATEGORIES: ExpenseCategory[] = [
  'TRAVEL_TICKETS',
  'TAXI',
  'ACCOMMODATION',
  'MEALS_WHILE_TRAVELLING',
  'PARKING',
  'ENTERTAINMENT',
  'OFFICE_SUPPLIES',
  'SOFTWARE_AND_SUBSCRIPTIONS',
  'PHONE_AND_INTERNET',
  'TRAINING',
  'OTHER_TRAVEL_EXPENSE',
  'OTHER_EXPENSE'
]

const centsToField = (cents: number | null) =>
  cents === null ? '' : (cents / 100).toFixed(2).replace('.', ',')

const fieldToCents = (raw: string): number | null | undefined => {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const normalized = trimmed.replace(/\s/g, '').replace(',', '.')
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return undefined
  return Math.round(Number(normalized) * 100)
}

type VatRowErrors = { gross?: string; vat?: string }

type ExpenseFieldErrors = { amount?: string; rows: Record<number, VatRowErrors> }

const NO_FIELD_ERRORS: ExpenseFieldErrors = { rows: {} }

const asInputErrors = (message?: string) => (message ? { message } : undefined)

const expenseKey = (slug: string, claimId: number, expenseId: number) => [
  slug,
  'expense-claim-expense',
  claimId,
  expenseId
]

type VatDraft = { rate: string; gross: string; vat: string }

const draftFromRow = (row: ExpenseVatRow): VatDraft => ({
  rate: String(Number(row.vat_rate)),
  gross: centsToField(row.gross_amount),
  vat: centsToField(row.vat_amount)
})

export const ExpenseEditorPage: React.FC = () => {
  const [t] = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { slug, data: business } = useBusinessContext()
  const { claimId: claimIdParam, expenseId: expenseIdParam } = useParams<{
    claimId: string
    expenseId: string
  }>()
  const claimId = Number(claimIdParam)
  const isNew = !expenseIdParam || expenseIdParam === 'new'
  const expenseId = isNew ? null : Number(expenseIdParam)
  const search = new URLSearchParams(location.search)
  const presetTrip = search.get('trip')
  const fresh = search.get('fresh') === '1'
  const canEdit = usePermission(PermissionSet.EXPENSE_CLAIMS_EDIT)
  const canPickFiles = usePermission(PermissionSet.FILES_EDIT)
  const { data: currentUser } = useCurrentUser()
  const fileInput = useRef<HTMLInputElement>(null)
  const pageInput = useRef<HTMLInputElement>(null)
  const touched = useRef(new Set<string>())
  const readFailedToasted = useRef<number | null>(null)

  const claimQuery = useQuery({
    queryKey: claimKey(slug, claimId),
    queryFn: () => fetchExpenseClaim(slug, claimId),
    enabled: Number.isInteger(claimId)
  })
  const expenseQuery = useQuery({
    queryKey: expenseKey(slug, claimId, expenseId ?? 0),
    queryFn: () => fetchExpenseClaimExpense(slug, claimId, expenseId as number),
    enabled: expenseId !== null,
    refetchInterval: query => {
      const reading = query.state.data?.reading.status
      return reading === 'PENDING' || reading === 'IN_PROGRESS' ? 2000 : false
    }
  })
  const tripsQuery = useQuery({
    queryKey: [slug, 'expense-claim-trips', claimId],
    queryFn: () => fetchExpenseClaimTrips(slug, claimId),
    enabled: Number.isInteger(claimId)
  })
  const receiptQuery = useQuery({
    queryKey: [slug, 'expense-claim-receipt', claimId, expenseId],
    queryFn: () => fetchExpenseClaimReceipt(slug, claimId, expenseId as number),
    enabled: expenseId !== null
  })

  const claim = claimQuery.data
  const expense = expenseQuery.data
  const isOwnDraft =
    !!claim && claim.status === 'DRAFT' && claim.claimant.id === currentUser?.id && canEdit

  const [merchant, setMerchant] = useState('')
  const [purchaseDate, setPurchaseDate] = useState('')
  const [amount, setAmount] = useState('')
  const [category, setCategory] = useState('')
  const [description, setDescription] = useState('')
  const [tripId, setTripId] = useState(presetTrip ?? '')
  const [tagIds, setTagIds] = useState<number[]>([])
  const [vatRows, setVatRows] = useState<VatDraft[]>([{ rate: '', gross: '', vat: '' }])
  const [fieldErrors, setFieldErrors] = useState<ExpenseFieldErrors>(NO_FIELD_ERRORS)
  const [readFields, setReadFields] = useState<Set<string>>(new Set())
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [showFiles, setShowFiles] = useState(false)
  const [fileTarget, setFileTarget] = useState<'create' | 'replace'>('create')

  useEffect(() => {
    if (!expense) return
    setMerchant(expense.merchant)
    setPurchaseDate(expense.purchase_date ?? '')
    setAmount(centsToField(expense.amount))
    setCategory(expense.category ?? '')
    setDescription(expense.description)
    setTripId(expense.trip_id ? String(expense.trip_id) : '')
    setTagIds(expense.tags.map(tag => tag.id))
    setVatRows(
      expense.vat_rows.length > 0
        ? expense.vat_rows.map(draftFromRow)
        : [{ rate: '', gross: '', vat: '' }]
    )
    touched.current = new Set()
  }, [expense?.id])

  useEffect(() => {
    const reading = expense?.reading
    if (!expense || !reading || reading.status !== 'COMPLETED') return
    const mark = (field: string) => setReadFields(current => new Set(current).add(field))
    if (!touched.current.has('merchant') && !expense.merchant && reading.merchant) {
      setMerchant(reading.merchant)
      mark('merchant')
    }
    if (!touched.current.has('purchaseDate') && !expense.purchase_date && reading.purchase_date) {
      setPurchaseDate(reading.purchase_date)
      mark('purchaseDate')
    }
    if (!touched.current.has('amount') && expense.amount === null && reading.amount !== null) {
      setAmount(centsToField(reading.amount))
      mark('amount')
    }
    if (!touched.current.has('category') && !expense.category && reading.suggested_category) {
      setCategory(reading.suggested_category)
      mark('category')
    }
    if (
      !touched.current.has('vat') &&
      expense.vat_rows.length === 0 &&
      reading.vat_rows.length > 0
    ) {
      setVatRows(reading.vat_rows.map(draftFromRow))
      mark('vat')
    }
  }, [expense])

  useEffect(() => {
    if (!receiptQuery.data) return
    const url = URL.createObjectURL(receiptQuery.data)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [receiptQuery.data])

  useEffect(() => {
    if (expense?.reading?.status !== 'FAILED' || expenseId === null) return
    if (readFailedToasted.current === expenseId) return
    readFailedToasted.current = expenseId
    toast.error(t('travelAndExpenses.expense.readFailed'))
  }, [expense?.reading?.status, expenseId, t])

  const vatOptions = useMemo(() => {
    const onDate = dayjs(purchaseDate || undefined)
    const config = [...(business?.country_config?.vat_configs ?? [])]
      .sort((left, right) => dayjs(right.effective_from).diff(dayjs(left.effective_from)))
      .find(item => dayjs(item.effective_from).isSameOrBefore(onDate, 'day'))
    return (config?.vat_rate_options ?? []).map(option => ({
      value: String(option.rate),
      label: t(`constants.vatRate.${option.label}`, { rate: option.rate })
    }))
  }, [business?.country_config, purchaseDate, t])

  const messageFrom = (saveError: unknown) =>
    [
      'detail',
      'file',
      'trip_id',
      'merchant',
      'purchase_date',
      'amount',
      'category',
      'vat_rows',
      'tag_ids',
      'attachment_id'
    ]
      .map(field => expenseClaimFieldError(saveError, field))
      .find((value): value is string => Boolean(value)) ?? t('travelAndExpenses.loadError')

  const showError = (saveError: unknown) => {
    toast.error(messageFrom(saveError))
  }

  const refresh = (saved?: ExpenseClaimExpense) => {
    queryClient.invalidateQueries({ queryKey: claimKey(slug, claimId) })
    queryClient.invalidateQueries({ queryKey: [slug, 'expense-claim-trips', claimId] })
    queryClient.invalidateQueries({ queryKey: expenseClaimExpensesKey(slug, claimId) })
    queryClient.invalidateQueries({ queryKey: [slug, 'expense-claims'] })
    const id = saved?.id ?? expenseId
    if (id) {
      if (saved) {
        queryClient.setQueryData(expenseKey(slug, claimId, id), saved)
        queryClient.setQueryData(
          expenseClaimExpensesKey(slug, claimId),
          (current: ExpenseClaimExpense[] | undefined) => {
            if (!current) return current
            const index = current.findIndex(item => item.id === saved.id)
            if (index === -1) return [...current, saved]
            return current.map(item => (item.id === saved.id ? saved : item))
          }
        )
      }
      queryClient.invalidateQueries({ queryKey: expenseKey(slug, claimId, id) })
      queryClient.invalidateQueries({ queryKey: [slug, 'expense-claim-receipt', claimId, id] })
    }
  }

  const refetchAfterExpenseChange = async () => {
    await queryClient.refetchQueries({ queryKey: expenseClaimExpensesKey(slug, claimId) })
    if (tripId) {
      await queryClient.refetchQueries({
        queryKey: [slug, 'expense-claim-trip', claimId, Number(tripId)]
      })
    }
  }

  const expenseEditorBackPath = () =>
    tripId
      ? getExpenseClaimTripPageUrl(slug, claimId, tripId)
      : getExpenseClaimPageUrl(slug, claimId)

  const leave = () => {
    history.push(expenseEditorBackPath())
  }

  const upload = useMutation({
    mutationFn: (file: File) =>
      createExpenseClaimExpense(slug, claimId, file, presetTrip ? Number(presetTrip) : null),
    onSuccess: saved => {
      refresh(saved)
      history.replace(getExpenseClaimExpensePageUrl(slug, claimId, saved.id, { fresh: 1 }))
    },
    onError: showError
  })

  const createFromFiles = useMutation({
    mutationFn: (attachmentId: number) =>
      createExpenseClaimExpenseFromAttachment(
        slug,
        claimId,
        attachmentId,
        presetTrip ? Number(presetTrip) : null
      ),
    onSuccess: saved => {
      refresh(saved)
      history.replace(getExpenseClaimExpensePageUrl(slug, claimId, saved.id, { fresh: 1 }))
    },
    onError: showError
  })

  const amountProblem = (raw: string) => {
    if (raw.trim().startsWith('-')) return t('travelAndExpenses.expense.amountNegative')
    if (fieldToCents(raw) === undefined) return t('travelAndExpenses.expense.amountInvalid')
    return undefined
  }

  const buildExpense = ():
    | { errors: ExpenseFieldErrors }
    | { payload: ExpenseClaimExpenseWrite } => {
    const errors: ExpenseFieldErrors = { amount: amountProblem(amount), rows: {} }
    const cents = errors.amount ? null : fieldToCents(amount) ?? null
    const rows: NonNullable<ExpenseClaimExpenseWrite['vat_rows']> = []
    vatRows.forEach((row, index) => {
      if (!row.rate) return
      const isSingleRow = vatRows.length === 1
      const rowErrors: VatRowErrors = {}
      if (!isSingleRow) rowErrors.gross = amountProblem(row.gross)
      const gross = isSingleRow
        ? cents ?? fieldToCents(row.gross) ?? null
        : rowErrors.gross
        ? null
        : fieldToCents(row.gross) ?? null
      if (!rowErrors.gross && !(gross && gross > 0)) {
        if (isSingleRow)
          errors.amount = errors.amount ?? t('travelAndExpenses.expense.grossRequired')
        else rowErrors.gross = t('travelAndExpenses.expense.grossRequired')
      }
      rowErrors.vat = row.vat.trim() === '' ? undefined : amountProblem(row.vat)
      if (rowErrors.gross || rowErrors.vat) errors.rows[index] = rowErrors
      if (!gross || gross <= 0 || rowErrors.vat) return
      const vat = fieldToCents(row.vat) ?? null
      rows.push({
        vat_rate: Number(row.rate).toFixed(2),
        gross_amount: gross,
        ...(vat === null ? {} : { vat_amount: vat })
      })
    })
    if (errors.amount || Object.keys(errors.rows).length > 0) return { errors }
    return {
      payload: {
        merchant,
        purchase_date: purchaseDate || null,
        description,
        category: (category || null) as ExpenseCategory | null,
        amount: cents,
        trip_id: tripId ? Number(tripId) : null,
        tag_ids: tagIds,
        vat_rows: rows
      }
    }
  }

  const save = useMutation({
    mutationFn: (payload: ExpenseClaimExpenseWrite) =>
      updateExpenseClaimExpense(slug, claimId, expenseId as number, payload),
    onSuccess: async saved => {
      refresh(saved)
      await refetchAfterExpenseChange()
      toast.success(t('travelAndExpenses.expense.saveSuccess'))
      leave()
    },
    onError: saveError => {
      const amountError = expenseClaimFieldError(saveError, 'amount')
      if (amountError) {
        setFieldErrors({ amount: amountError, rows: {} })
        return
      }
      showError(saveError)
    }
  })

  const saveExpense = () => {
    const built = buildExpense()
    if ('errors' in built) {
      setFieldErrors(built.errors)
      return
    }
    setFieldErrors(NO_FIELD_ERRORS)
    save.mutate(built.payload)
  }

  const replaceFile = useMutation({
    mutationFn: (file: File) =>
      replaceExpenseClaimReceipt(slug, claimId, expenseId as number, file),
    onSuccess: saved => refresh(saved),
    onError: showError
  })

  const replaceFromFiles = useMutation({
    mutationFn: (attachmentId: number) =>
      replaceExpenseClaimReceiptFromAttachment(slug, claimId, expenseId as number, attachmentId),
    onSuccess: saved => refresh(saved),
    onError: showError
  })

  const addPage = useMutation({
    mutationFn: (file: File) =>
      appendExpenseClaimReceiptPage(slug, claimId, expenseId as number, file),
    onSuccess: saved => refresh(saved),
    onError: showError
  })

  const remove = useMutation({
    mutationFn: () => deleteExpenseClaimExpense(slug, claimId, expenseId as number),
    onSuccess: async () => {
      refresh()
      await refetchAfterExpenseChange()
      leave()
    },
    onError: showError
  })

  const reading = expense?.reading
  const readingNow = reading?.status === 'PENDING' || reading?.status === 'IN_PROGRESS'
  const readInfo = (field: string) =>
    readFields.has(field) ? t('travelAndExpenses.expense.readFromReceipt') : undefined
  const editField = (field: string) => {
    touched.current.add(field)
    if (field === 'amount' || field === 'vat') setFieldErrors(NO_FIELD_ERRORS)
    setReadFields(current => {
      if (!current.has(field)) return current
      const next = new Set(current)
      next.delete(field)
      return next
    })
  }
  const receiptType = expense?.attachment.type ?? ''
  const receiptBusy = upload.isPending || createFromFiles.isPending
  const { open, getRootProps, getInputProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: false,
    disabled: !isOwnDraft || receiptBusy,
    accept: ['image/*', 'application/pdf'],
    onDrop: files => {
      const file = files[0]
      if (file) upload.mutate(file)
    }
  })
  const openFiles = (target: 'create' | 'replace') => {
    setFileTarget(target)
    setShowFiles(true)
  }
  const onFilesChosen = (ids: number[]) => {
    if (ids.length === 0) {
      setShowFiles(false)
      return
    }
    if (ids.length > 1) {
      toast.error(t('travelAndExpenses.expense.oneFile'))
      return
    }
    setShowFiles(false)
    if (fileTarget === 'replace') replaceFromFiles.mutate(ids[0])
    else createFromFiles.mutate(ids[0])
  }

  return (
    <SingleColumnLayout header={t('travelAndExpenses.title')} headerTo={expenseEditorBackPath()}>
      <PageScroll>
        <AnimatedContentLoader isLoading={claimQuery.isLoading || expenseQuery.isLoading}>
          <PageContent>
            <PageTop>
              <PageTopMain>
                <PageTitle>{t('travelAndExpenses.expense.title')}</PageTitle>
                {claimQuery.data && (
                  <PageSubtitle>
                    <TextLink to={getExpenseClaimPageUrl(slug, claimId)}>
                      {t('travelAndExpenses.trip.claimLine', {
                        number: claimQuery.data.number
                      })}
                    </TextLink>
                  </PageSubtitle>
                )}
              </PageTopMain>
            </PageTop>
            {isNew ? (
              <Card>
                <ReceiptDrop
                  $isDragActive={isDragActive}
                  $appearance="dropzone"
                  {...getRootProps()}
                >
                  <DropBody>
                    <FaReceipt size={28} />
                    <DropTitle>{t('travelAndExpenses.expense.dropTitle')}</DropTitle>
                    {receiptBusy ? (
                      <Help>{t('travelAndExpenses.expense.reading')}</Help>
                    ) : (
                      <div className="add-more">
                        <AttachmentDropHint
                          onOpen={() => open()}
                          onShowArchive={canPickFiles ? () => openFiles('create') : undefined}
                        />
                      </div>
                    )}
                    <Help>{t('travelAndExpenses.expense.acceptedTypes')}</Help>
                  </DropBody>
                  <input {...getInputProps()} />
                </ReceiptDrop>
                <Help>{t('travelAndExpenses.expense.uploadHelp')}</Help>
              </Card>
            ) : (
              <Columns>
                <MainColumn>
                  <Card>
                    {previewUrl && receiptType.startsWith('image/') && (
                      <Preview src={previewUrl} alt="" />
                    )}
                    {previewUrl && receiptType === 'application/pdf' && (
                      <PdfFrame src={previewUrl} title={t('travelAndExpenses.expense.preview')} />
                    )}
                    <ButtonRow>
                      {canPickFiles ? (
                        <DropdownMenu
                          items={[
                            <Button
                              key="computer"
                              type="button"
                              onClick={() => fileInput.current?.click()}
                            >
                              {t('travelAndExpenses.expense.fromComputer')}
                            </Button>,
                            <Button key="files" type="button" onClick={() => openFiles('replace')}>
                              {t('travelAndExpenses.expense.fromFiles')}
                            </Button>
                          ]}
                        >
                          <Button
                            type="button"
                            intent="primary"
                            isSecondary
                            disabled={!isOwnDraft || replaceFromFiles.isPending}
                            iconRight={<FaCaretDown />}
                          >
                            {t('travelAndExpenses.expense.replaceFile')}
                          </Button>
                        </DropdownMenu>
                      ) : (
                        <Button
                          type="button"
                          intent="primary"
                          isSecondary
                          disabled={!isOwnDraft}
                          onClick={() => fileInput.current?.click()}
                        >
                          {t('travelAndExpenses.expense.replaceFile')}
                        </Button>
                      )}
                      <Button
                        type="button"
                        intent="primary"
                        isSecondary
                        disabled={!isOwnDraft}
                        onClick={() => pageInput.current?.click()}
                      >
                        {t('travelAndExpenses.expense.addPage')}
                      </Button>
                    </ButtonRow>
                    <Help>{t('travelAndExpenses.expense.oneAttachment')}</Help>
                    <HiddenFile
                      ref={fileInput}
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={event => {
                        const file = event.target.files?.[0]
                        if (file) replaceFile.mutate(file)
                      }}
                    />
                    <HiddenFile
                      ref={pageInput}
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={event => {
                        const file = event.target.files?.[0]
                        if (file) addPage.mutate(file)
                      }}
                    />
                  </Card>
                  <Card>
                    {readingNow && (
                      <Alert type="magic" title={t('travelAndExpenses.expense.reading')} />
                    )}
                    {reading?.status === 'COMPLETED' && (
                      <Alert
                        type="magic"
                        title={t('travelAndExpenses.expense.readDone')}
                        description={t('travelAndExpenses.expense.readDoneHelp')}
                      />
                    )}
                    <InputGroup>
                      <Input
                        name="merchant"
                        label={t('travelAndExpenses.expense.merchant')}
                        info={readInfo('merchant')}
                        value={merchant}
                        disabled={!isOwnDraft}
                        onChange={event => {
                          editField('merchant')
                          setMerchant(event.target.value)
                        }}
                      />
                      <Input
                        name="purchase_date"
                        type="date"
                        label={t('travelAndExpenses.expense.purchaseDate')}
                        info={readInfo('purchaseDate')}
                        value={purchaseDate}
                        disabled={!isOwnDraft}
                        onChange={event => {
                          editField('purchaseDate')
                          setPurchaseDate(event.target.value)
                        }}
                      />
                    </InputGroup>
                    <Input
                      name="amount"
                      label={t('travelAndExpenses.expense.amount')}
                      info={readInfo('amount')}
                      value={amount}
                      disabled={!isOwnDraft}
                      errors={asInputErrors(fieldErrors.amount)}
                      onChange={event => {
                        editField('amount')
                        setAmount(event.target.value)
                      }}
                    />
                    {vatRows.map((row, index) => (
                      <InputGroup key={index}>
                        <Select
                          name={`vat_rate_${index}`}
                          label={t('travelAndExpenses.expense.vatRate')}
                          info={readInfo('vat')}
                          value={row.rate}
                          disabled={!isOwnDraft}
                          onChange={event => {
                            editField('vat')
                            setVatRows(current =>
                              current.map((item, itemIndex) =>
                                itemIndex === index ? { ...item, rate: event.target.value } : item
                              )
                            )
                          }}
                        >
                          <option value="">{t('travelAndExpenses.expense.vatRateEmpty')}</option>
                          {vatOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                        {vatRows.length > 1 && (
                          <Input
                            name={`vat_gross_${index}`}
                            label={t('travelAndExpenses.expense.rowGross')}
                            value={row.gross}
                            disabled={!isOwnDraft}
                            errors={asInputErrors(fieldErrors.rows[index]?.gross)}
                            onChange={event => {
                              editField('vat')
                              setVatRows(current =>
                                current.map((item, itemIndex) =>
                                  itemIndex === index
                                    ? { ...item, gross: event.target.value }
                                    : item
                                )
                              )
                            }}
                          />
                        )}
                        <Input
                          name={`vat_amount_${index}`}
                          label={t('travelAndExpenses.expense.vat')}
                          info={readInfo('vat')}
                          value={row.vat}
                          disabled={!isOwnDraft}
                          errors={asInputErrors(fieldErrors.rows[index]?.vat)}
                          onChange={event => {
                            editField('vat')
                            setVatRows(current =>
                              current.map((item, itemIndex) =>
                                itemIndex === index ? { ...item, vat: event.target.value } : item
                              )
                            )
                          }}
                        />
                      </InputGroup>
                    ))}
                    {isOwnDraft && (
                      <Button
                        type="button"
                        intent="primary"
                        isSecondary
                        onClick={() =>
                          setVatRows(current => [...current, { rate: '', gross: '', vat: '' }])
                        }
                      >
                        {t('travelAndExpenses.expense.addVatRow')}
                      </Button>
                    )}
                    <Select
                      name="category"
                      label={t('travelAndExpenses.expense.category')}
                      info={readInfo('category')}
                      value={category}
                      disabled={!isOwnDraft}
                      onChange={event => {
                        editField('category')
                        setCategory(event.target.value)
                      }}
                    >
                      <option value="">{t('travelAndExpenses.expense.categoryEmpty')}</option>
                      {CATEGORIES.map(item => (
                        <option key={item} value={item}>
                          {t(`travelAndExpenses.expense.categories.${item}`)}
                        </option>
                      ))}
                    </Select>
                    {category === 'OTHER_EXPENSE' && (
                      <Help>{t('travelAndExpenses.expense.otherExpenseHelp')}</Help>
                    )}
                    <TagSelector
                      label={t('travelAndExpenses.trip.tags')}
                      value={tagIds}
                      isReadOnly={!isOwnDraft}
                      onChange={setTagIds}
                    />
                    <Help>{t('travelAndExpenses.expense.tagsHelp')}</Help>
                    <Select
                      name="trip_id"
                      label={t('travelAndExpenses.expense.linkTrip')}
                      value={tripId}
                      disabled={!isOwnDraft}
                      onChange={event => setTripId(event.target.value)}
                    >
                      <option value="">{t('travelAndExpenses.expense.noTrip')}</option>
                      {(tripsQuery.data ?? []).map(trip => (
                        <option key={trip.id} value={trip.id}>
                          {trip.description}
                        </option>
                      ))}
                    </Select>
                    <Input
                      name="description"
                      label={t('travelAndExpenses.expense.description')}
                      value={description}
                      disabled={!isOwnDraft}
                      onChange={event => setDescription(event.target.value)}
                    />
                    {isOwnDraft && (
                      <ButtonRow>
                        <Button
                          type="button"
                          intent="primary"
                          isSecondary
                          onClick={() => (fresh ? remove.mutate() : leave())}
                        >
                          {t('travelAndExpenses.expense.cancel')}
                        </Button>
                        <Button
                          type="button"
                          intent="primary"
                          showSpinner={save.isPending}
                          onClick={saveExpense}
                        >
                          {t('travelAndExpenses.expense.save')}
                        </Button>
                      </ButtonRow>
                    )}
                  </Card>
                </MainColumn>
              </Columns>
            )}
          </PageContent>
        </AnimatedContentLoader>
      </PageScroll>
      {canPickFiles && (
        <ModalV2
          header={t('attachmentSelector.title')}
          width={700}
          height={900}
          isVisible={showFiles}
          handleOnClose={() => setShowFiles(false)}
        >
          <FileSelector slug={slug} onClose={onFilesChosen} />
        </ModalV2>
      )}
    </SingleColumnLayout>
  )
}

const ReceiptDrop = styled(StyledEmptyAttachmentSelector)`
  flex: none;
`

const DropBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.metalGray};
`

const DropTitle = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.metalGray};
`

const HiddenFile = styled.input`
  display: none;
`

const Preview = styled.img`
  max-width: 100%;
  align-self: center;
`

const PdfFrame = styled.iframe`
  width: 100%;
  height: ${({ theme }) => theme.size.documentPreviewHeight}rem;
  border: 0;
`

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
